import { createClient } from '@openauthjs/openauth/client';

const AUTH_URL = (import.meta.env.VITE_AUTH_URL || '').replace(/\/$/, '');

const client = createClient({
  clientID: 'joyia-web',
  issuer: AUTH_URL,
});

export class AuthManager {
  private accessToken: string | undefined;
  private refreshPromise: Promise<string | undefined> | null = null;

  async initialize(): Promise<boolean> {
    // Check for OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state) {
      console.log('[Auth] OAuth callback detected');
      const challengeStr = sessionStorage.getItem('challenge');

      if (!challengeStr) {
        console.error('[Auth] No challenge found');
        return false;
      }

      const challenge = JSON.parse(challengeStr);

      if (state === challenge.state && challenge.verifier) {
        console.log('[Auth] Exchanging code for tokens...');
        const exchanged = await client.exchange(
          code,
          window.location.origin,
          challenge.verifier
        );

        if (!exchanged.err && exchanged.tokens) {
          console.log('[Auth] Token exchange successful');
          this.accessToken = exchanged.tokens.access;
          localStorage.setItem('refresh', exchanged.tokens.refresh);

          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          return true;
        } else {
          console.error('[Auth] Token exchange failed:', exchanged.err);
          return false;
        }
      }
    }

    // Try to refresh existing tokens
    const refreshToken = localStorage.getItem('refresh');
    if (refreshToken) {
      console.log('[Auth] Attempting token refresh...');
      const newToken = await this.refreshTokens();
      if (newToken) {
        console.log('[Auth] Token refresh successful');
        return true;
      }
    }

    return false;
  }

  async login(): Promise<void> {
    console.log('[Auth] Starting login flow...');
    const { challenge, url } = await client.authorize(window.location.origin, 'code', {
      pkce: true,
    });
    sessionStorage.setItem('challenge', JSON.stringify(challenge));
    window.location.href = url;
  }

  async getToken(): Promise<string | undefined> {
    if (this.accessToken) {
      return this.accessToken;
    }

    // Check if we're in an OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('code') && urlParams.has('state')) {
      return undefined;
    }

    // Try to refresh
    const token = await this.refreshTokens();
    if (!token) {
      await this.login();
      return undefined;
    }

    return token;
  }

  private async refreshTokens(): Promise<string | undefined> {
    // If there's already a refresh in progress, wait for it
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refresh = localStorage.getItem('refresh');
    if (!refresh) {
      console.log('[Auth] No refresh token available');
      return undefined;
    }

    this.refreshPromise = this.performTokenRefresh(refresh);

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(refresh: string): Promise<string | undefined> {
    try {
      const next = await client.refresh(refresh, {
        access: this.accessToken,
      });

      if (next.err) {
        console.error('[Auth] Token refresh failed:', next.err);
        localStorage.removeItem('refresh');
        this.accessToken = undefined;
        return undefined;
      }

      if (!next.tokens) {
        console.log('[Auth] No tokens returned from refresh');
        return this.accessToken;
      }

      localStorage.setItem('refresh', next.tokens.refresh);
      this.accessToken = next.tokens.access;

      return next.tokens.access;
    } catch (error) {
      console.error('[Auth] Error during token refresh:', error);
      localStorage.removeItem('refresh');
      this.accessToken = undefined;
      return undefined;
    }
  }

  logout(): void {
    localStorage.removeItem('refresh');
    this.accessToken = undefined;
    window.location.replace('/');
  }
}

// Export singleton instance
export const authManager = new AuthManager();
